import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useLocation } from 'react-router-dom';
import jobs from '../../../data/jobs';
import JobFilter from './JobFilter';
import ContactPage from '../../../components/Global/BannerCard/DynamicHeader/ContactPage1';

export default function JobList() {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const initialType = params.get('type') || '';

  const [filters, setFilters] = useState({
    type: initialType,
    location: '',
    search: '',
  });

  const [seo, setSeo] = useState(null);

  useEffect(() => {
    async function fetchSeoData() {
      const data = await new Promise((resolve) =>
        setTimeout(() => {
          resolve({
            title: 'Job Openings | Current Opportunities at V Adithya Private Limited',
            description: 'Browse the latest job openings at V Adithya Private Limited. Find the perfect role and apply online today.',
            keywords: 'job openings, IT jobs, non-IT jobs, V Adithya careers, Hyderabad, Bengaluru, Pune',
            canonical: 'https://yourdomain.com/job-list',
            image: 'https://yourdomain.com/images/seo-banner.jpg',
          });
        }, 100)
      );
      setSeo(data);
    }

    fetchSeoData();
  }, []);

  const filteredJobs = jobs.filter((job) => {
    const matchesType = !filters.type || job.department.toLowerCase() === filters.type.toLowerCase();

    const matchesLocation =
      !filters.location ||
      job.locations?.some(
        (loc) => loc.trim().toLowerCase() === filters.location.trim().toLowerCase()
      );

    const matchesSearch =
      !filters.search || job.title.toLowerCase().includes(filters.search.toLowerCase());

    return matchesType && matchesLocation && matchesSearch;
  });

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    itemListElement: filteredJobs.map((job, index) => ({
      "@type": "ListItem",
      position: index + 1,
      item: {
        "@type": "JobPosting",
        title: job.title,
        description: job.description || "Explore this opportunity at V Adithya Private Limited.",
        identifier: {
          "@type": "PropertyValue",
          name: "V Adithya Private Limited",
          value: job.id,
        },
        datePosted: job.postedDate,
        employmentType: "Full-time",
        hiringOrganization: {
          "@type": "Organization",
          name: "V Adithya Private Limited",
          sameAs: "https://yourdomain.com",
        },
        jobLocation: {
          "@type": "Place",
          address: {
            "@type": "PostalAddress",
            addressLocality: job.locations?.[0] || 'India',
            addressCountry: "IN",
          },
        },
      },
    })),
  };

  return (
    <>
      {seo && (
        <Helmet>
          <title>{seo.title}</title>
          <meta name="description" content={seo.description} />
          <meta name="keywords" content={seo.keywords} />
          <link rel="canonical" href={seo.canonical} />

          <meta property="og:title" content={seo.title} />
          <meta property="og:description" content={seo.description} />
          <meta property="og:url" content={seo.canonical} />
          <meta property="og:type" content="website" />
          <meta property="og:image" content={seo.image} />

          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:title" content={seo.title} />
          <meta name="twitter:description" content={seo.description} />
          <meta name="twitter:image" content={seo.image} />

          <script type="application/ld+json">{JSON.stringify(structuredData)}</script>
        </Helmet>
      )}

      <ContactPage
        title="CURRENT OPPORTUNITIES"
        highlightWord="OPPORTUNITIES"
        backgroundImage="data:image/jpeg;base64,/9j/4AAQSkZJRgABAQAAAQABAAD/2wCEAAkGBw8NDw0NDQ0QDQ0NEBINDQ0NEBANDQ0NFRIWFhURFRUYHSggGBolGxUVITEhJSkrOi4uFx8/OD84Nyg5OisBCgoKDg0NFQ0NFSsZFRkrLS03LSsrKy03Ny0rKystLS03LS0rNy0tLS0tNysrLTc3Ny0tNystKy03Ny0tKy0tK//AABEIAKgBLAMBIgACEQEDEQH/xAAYAAEBAQEBAAAAAAAAAAAAAAAAAQIHA//EACYQAQAABQUAAgMBAQEAAAAAAAADMdHh8AIUQVOSARFRYcGhsSH/xAAVAQEBAAAAAAAAAAAAAAAAAAAAAf/EABQRAQAAAAAAAAAAAAAAAAAAAAD/2gAMAwEAAhEDEQA/AOIAKKACoAigApZFsAAC3LJfktyC3Vm/LWTAuJfkr+QU+/8Av8TJrX8/oEVMmZMD7/gnxn/pkwLFyxcAABAARUAAFQBAAAAUURQAMmCiZMyYirbhMmW5BS3Bky3IBbgyZbkFvwJflcmBklyTOTWv5AyS1/jOTWv5/QGSEyZkwW3CZItyZMC3BfgtyX5AQyZkwATJgqGTBT5D5QABABAUAFEVQABRARSwW5BSxk0tyCljJluQLr9pfkyYH2qZMyYC5/iZMyf6ADJmTAsFuTJgWLluUvyCoZMyYAICoAoCIAAAICgAAAoCioAKBbkQWyZMyYKJk1yYFxL8rkwBMmuTAM/xMmuTAEya5MCwmTMmC5JDJmTADJgCAKAAICAACAAoAAAKIKKAClkAUQEUEBbqyoKIAKgAqAAAAgCoAoAAIAAIAIAioDQn2AoCgAgogCgKKgAomTAFQEUTJgKIuTAEyagCAAZMFAAEVEFQFAABPtUQQABftAGkAFAAABRAFEUAD7UFQAVAAABUAAAAQFQAAEAEBU+w+wPtAAAAABfg+EX4BRPgBRBRQEBUFFEEFEFFEEFEFFEEFEAAAAAAQFD7T7A+flFQAAAAAAAAFEAVWVBRFAAAAAAAAAAAAAAAAABAVBAUQAAAAABAURQAAAAAAFQBVZUFEAURfkARQBFAE+AFEAAAEAFQAAAAAAQAAHvs4vTE8aqGzi9MTxqoAGzi9MTxqouzi9MTxqogC7OL1RPGqhs4vTE8aqABs4vTE8aqGzi9MTxqoAGzi9MTxqobOL0xPGqgAbOL0xPGqhs4vTE8aqABs4vVE8aqGzi9UTxqoAGzi9UTxqobOL1RPGqgAbOL1RPGqhs4vVE8aqABs4vVE8aqGzi9UTxqoAGzi9UTxqobOL1RPGqgAbOL1RPGqhs4vVE8aqABs4vTE8aqGzi9MTxqoAGzi9MTxqobOL0xPGqgAbOL0xPGqhs4vTE8aqABs4vTE8aqGzi9UTxqogC7OL1RPGqibOL0xPGqgAbOL0xPGqhs4vTE8aqABs4vTE8aqGzi9MTxqoAP/9k="
      />

      <div className="px-4 md:px-8 lg:px-16 py-6 bg-white min-h-screen">
        <JobFilter onFilterChange={setFilters} />

        <p className="mb-4 font-semibold text-gray-700 text-sm md:text-base">
          {filteredJobs.length} openings found
        </p>

        <div className="space-y-4">
          {filteredJobs.length > 0 ? (
            filteredJobs.map((job) => (
              <div
                key={job.id}
                className="flex flex-col md:flex-row justify-between items-start md:items-center p-4 border rounded-md shadow-sm hover:shadow-md transition bg-gray-50"
              >
                <div className="mb-4 md:mb-0">
                  <Link to={`/careers/job-details/${job.id}`}>
                    <h3 className="text-lg md:text-xl font-semibold text-blue-600 hover:underline">
                      {job.title}
                    </h3>
                  </Link>
                  <p className="text-gray-700 text-sm">
                    {job.designation} | {job.experience}
                  </p>
                  <p className="text-sm text-gray-600">
                    {job.department} | Posted on: {job.postedDate}
                  </p>
                </div>
                <Link to={`/careers/job-details/${job.id}`}>
                  <button className="px-4 py-2 border border-blue-600 text-blue-600 rounded hover:bg-blue-600 hover:text-white transition text-sm">
                    Apply
                  </button>
                </Link>
              </div>
            ))
          ) : (
            <p className="text-center text-red-600 font-semibold">
              No jobs match your filters.
            </p>
          )}
        </div>
      </div>
    </>
  );
}
